import React, { ReactNode } from 'react';
import { noop } from 'lodash-es';
import { overlay } from 'overlay-kit';
import { cn, isMobileDevice } from '@zep/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../../_experimental/Popover';
import { ActionSheet, ActionSheetContent } from '../ActionSheet';

/**
 * 오버레이 콜백 함수들의 속성 타입 정의
 * overlay.open 함수에서 제공하는 콜백 함수 속성을 정의합니다.
 */
type OverlayCallbackProps = {
  isOpen: boolean; // 오버레이의 열림 상태
  close: () => void; // 오버레이를 닫는 함수
  unmount: () => void; // 오버레이를 언마운트하는 함수
};

/**
 * 오버레이 props를 받을 수 있는 컴포넌트 타입
 * React.cloneElement를 사용할 때 TypeScript 타입 오류를 방지하기 위한 타입입니다.
 * any를 사용하여 모든 props를 받을 수 있도록 합니다.
 */
type ComponentWithOverlayProps = React.ReactElement<any>;

/**
 * PopoverActionSheetAdapter의 props 인터페이스
 */
interface PopoverActionSheetAdapterProps {
  /**
   * 팝오버 또는 액션시트 내부에 표시될 컨텐츠
   * ReactNode 또는 콜백 함수 형태로 전달할 수 있습니다.
   * 함수 형태로 전달할 경우 overlay.open의 콜백 속성을 전달받을 수 있습니다.
   */
  children: ReactNode | ((props: OverlayCallbackProps) => ReactNode);

  /**
   * 팝오버를 열기 위한 트리거 요소
   * 모바일에서는 이 요소에 onClick 이벤트를 추가합니다.
   */
  TriggerRenderer: ({
    onClick
  }: {
    onClick: (e: React.MouseEvent) => void;
  }) => ReactNode;

  /**
   * 팝오버 위치 (데스크톱 환경에서만 적용)
   */
  side?: 'top' | 'right' | 'bottom' | 'left';

  /**
   * 팝오버 정렬 (데스크톱 환경에서만 적용)
   */
  align?: 'start' | 'center' | 'end';

  /**
   * 팝오버 오프셋 (데스크톱 환경에서만 적용)
   */
  sideOffset?: number;

  /**
   * 추가 CSS 클래스
   */
  className?: string;

  /**
   * 컨텐츠 홀더 사용 여부 (모바일 환경에서만 적용)
   */
  withContentHolder?: boolean;

  /**
   * 구분선 사용 여부 (모바일 환경에서만 적용)
   */
  withSeparator?: boolean;
}

/**
 * 반응형 Popover/ActionSheet 어댑터 컴포넌트
 *
 * 이 컴포넌트는 디바이스 타입에 따라 다른 UI 패턴을 적용합니다:
 * - 모바일: ActionSheet를 사용 (바텀시트, 하단에서 올라오는 형태)
 * - 데스크톱: Popover를 사용 (요소 근처에 표시되는 형태)
 *
 * 이렇게 구현한 이유:
 * 1. 모바일에서는 작은 화면 크기를 고려해 하단에서 올라오는 ActionSheet가 더 적합함
 * 2. 데스크톱에서는 마우스 포인터 근처에 표시되는 Popover가 사용성이 좋음
 * 3. 동일한 컴포넌트 인터페이스로 서로 다른 환경에 최적화된 UI를 제공할 수 있음
 */
export const PopoverActionSheetAdapter = ({
  children,
  TriggerRenderer,
  side = 'right',
  align = 'end',
  sideOffset,
  className,
  withContentHolder = false,
  withSeparator = true
}: PopoverActionSheetAdapterProps) => {
  // 모바일 디바이스인 경우
  if (isMobileDevice()) {
    /**
     * React.cloneElement를 사용하는 이유:
     * 1. trigger는 이미 생성된 React 요소(ReactElement)입니다
     * 2. 기존 ReactNode에 직접 이벤트 핸들러를 추가할 수 없습니다
     * 3. 새로운 div로 감싸는 방식은 DOM 구조와 스타일을 변경시킬 수 있습니다
     * 4. cloneElement는 기존 요소의 모든 속성을 유지하면서 새 속성을 추가합니다
     */
    return <TriggerRenderer onClick={(e: React.MouseEvent) => {
      // 이벤트 버블링 방지
      e.stopPropagation();
      // 추가 클릭 핸들러 실행

      // overlay 시스템을 사용하여 ActionSheet 표시
      overlay.open(({
        isOpen,
        close,
        unmount
      }) => <ActionSheet open={isOpen} onOpenChange={open => {
        if (!open) {
          close();
          unmount();
        }
      }}>
              <ActionSheetContent withContentHolder={withContentHolder} withSeparator={withSeparator} className={cn('border-0', className)}>
                {/* 
                 children 처리 로직:
                 1. 함수인 경우: 콜백 함수로 overlay 속성을 전달 (렌더 프롭 패턴)
                 2. 유효한 React 요소인 경우: cloneElement로 props 전달
                 3. 그 외 경우: 그대로 렌더링
                 */}
                {typeof children === 'function' ? children({
            isOpen,
            close,
            unmount
          }) : React.isValidElement(children) ? React.cloneElement(children as ComponentWithOverlayProps, {
            isOpen,
            close,
            unmount
          }) : children}
              </ActionSheetContent>
            </ActionSheet>);
    }} />;
  }

  /**
   * 데스크톱 환경에서 children 렌더링 처리
   *
   * 데스크톱에서는 Popover 컴포넌트가 함수형 children을 직접 처리할 수 없으므로
   * 함수형 children을 먼저 실행하여 ReactNode로 변환합니다.
   */
  const renderChildren = () => {
    // 함수형 children인 경우 호출하여 ReactNode 반환
    if (typeof children === 'function') {
      /**
       * 데스크톱 환경에서는 overlay.open의 콜백 속성이 없으므로
       * 빈 값이나 더미 함수를 제공하여 인터페이스를 통일합니다.
       *
       * isOpen: true - 팝오버가 열려있을 때만 렌더링되므로 항상 true
       * close/unmount: noop - 데스크톱에서는 Popover 컴포넌트가 자체 관리
       */
      return children({
        isOpen: true,
        close: noop,
        // 빈 함수 (es-toolkit에서 import)
        unmount: noop
      });
    }
    // 일반 ReactNode인 경우 그대로 반환
    return children;
  };

  // 데스크톱 환경에서는 Popover 컴포넌트 사용
  return <Popover data-sentry-element="Popover" data-sentry-component="PopoverActionSheetAdapter" data-sentry-source-file="PopoverActionSheetAdapter.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="PopoverActionSheetAdapter.tsx">
        {TriggerRenderer({
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
        }
      })}
      </PopoverTrigger>
      <PopoverContent side={side} align={align} sideOffset={sideOffset} className={cn('w-[250px] overflow-hidden rounded-[8px] border-none bg-white p-0 z-[100]', className)} data-sentry-element="PopoverContent" data-sentry-source-file="PopoverActionSheetAdapter.tsx">
        {/* 함수형 children을 처리하여 렌더링 */}
        {renderChildren()}
      </PopoverContent>
    </Popover>;
};